import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import axios from "../../api/axios";

/**
 * AboutContent - Reusable content for the About page
 * Fetches dynamic content from the API
 * Used by both PublicAbout (with PublicPageLayout) and MainLayout (for authenticated users)
 */
const AboutContent = () => {
    const { t, i18n } = useTranslation();
    const { auth } = useAuth();
    const isAuthenticated = !!auth?.accessToken;

    const [loading, setLoading] = useState(true);
    const [aboutData, setAboutData] = useState(null);
    const currentLang = i18n.language?.toUpperCase() || 'EN';

    // Convert Bootstrap badge class names to actual colors
    const getBadgeColor = (badgeColor) => {
        const colorMap = {
            'primary': '#0d6efd',
            'secondary': '#6c757d',
            'success': '#198754',
            'danger': '#dc3545',
            'warning': '#ffc107',
            'info': '#0dcaf0',
            'light': '#f8f9fa',
            'dark': '#212529'
        };
        // If it's already a hex color or RGB, return as-is
        if (badgeColor?.startsWith('#') || badgeColor?.startsWith('rgb')) {
            return badgeColor;
        }
        return colorMap[badgeColor] || '#6c757d';
    };

    // Check if badge needs dark text (for light/warning backgrounds)
    const needsDarkText = (badgeColor) => {
        return badgeColor === 'light' || badgeColor === 'warning';
    };

    useEffect(() => {
        const fetchAboutData = async () => {
            try {
                const response = await axios.get('/ContentManagement/about');
                setAboutData(response.data);
            } catch (error) {
                console.error('Error fetching about data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchAboutData();
    }, []);

    // Helper function to get localized text
    const getLocalizedText = (item, fieldBase) => {
        const field = `${fieldBase}${currentLang}`;
        return item[field] || item[`${fieldBase}EN`] || '';
    };

    // Helper function to construct full avatar URL (same pattern as ToolPassport logos)
    const getAvatarUrl = (avatarUrl) => {
        if (!avatarUrl) return null;
        // Replace backslashes with forward slashes for URL compatibility
        const normalizedPath = avatarUrl.replace(/\\/g, '/');
        return `${process.env.REACT_APP_SERVER_URLForLogo}/${normalizedPath}`;
    };

    if (loading) {
        return (
            <div className="container px-3 text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t("loading")}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container px-3">
            {/* Hero Section */}
            <div className="row mb-5">
                <div className="col-12 text-center">
                    <div className="py-4">
                        <img
                            src="/assets/img/OnlineCADCOM_FullLogo.png"
                            alt="Online-CADCOM"
                            style={{ height: '80px', marginBottom: '24px' }}
                        />
                        <h1 style={{ color: '#112d4e', fontWeight: 700, marginBottom: '16px' }}>
                            {t("aboutTitle")}
                        </h1>
                        <p className="lead text-muted" style={{ maxWidth: '700px', margin: '0 auto' }}>
                            {t("aboutSubtitle")}
                        </p>
                    </div>
                </div>
            </div>

            {/* What is Online-CADCOM */}
            <div className="row mb-5">
                <div className="col-lg-8 mx-auto">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-4 p-lg-5">
                            <h3 style={{ color: '#112d4e', fontWeight: 600, marginBottom: '20px' }}>
                                <i className="bi bi-lightbulb me-2"></i>
                                {t("aboutWhatIs")}
                            </h3>
                            <p className="text-secondary mb-4" style={{ fontSize: '1.1rem', lineHeight: '1.8' }}>
                                {t("aboutWhatIsDescription")}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Platform History Section */}
            {aboutData?.platformHistory && aboutData.platformHistory.length > 0 && (
                <div className="row mb-5">
                    <div className="col-lg-10 mx-auto">
                        <div className="card border-0 shadow-sm" style={{ backgroundColor: '#fafbfc' }}>
                            <div className="card-body p-4 p-lg-5">
                                <h3 className="text-center mb-4" style={{ color: '#112d4e', fontWeight: 600 }}>
                                    <i className="bi bi-clock-history me-2"></i>
                                    {t("aboutHistoryTitle")}
                                </h3>
                                <p className="text-center text-secondary mb-4">
                                    {t("aboutHistoryDescription")}
                                </p>

                                {/* Timeline */}
                                <div className="row g-4">
                                    {aboutData.platformHistory.map((era, index) => (
                                        <div key={era.id} className={`col-md-${index < 3 ? '4' : '6'}`}>
                                            <div className="card h-100 border-0 bg-white shadow-sm">
                                                <div className="card-body p-4">
                                                    <div className="d-flex align-items-center mb-3">
                                                        <span className={`badge me-2`} style={{ fontSize: '1rem', backgroundColor: getBadgeColor(era.badgeColor) }}>
                                                            {era.yearRange}
                                                        </span>
                                                        <span className="text-muted small">{getLocalizedText(era, 'phaseName')}</span>
                                                    </div>
                                                    <h6 style={{ color: '#112d4e', fontWeight: 600 }}>{getLocalizedText(era, 'title')}</h6>
                                                    <p className="text-muted small mb-0">{getLocalizedText(era, 'description')}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Research Publications Link */}
                                <div className="text-center mt-4">
                                    <Link
                                        to="/ResearchPublications"
                                        className="btn btn-outline-primary btn-lg px-4"
                                        style={{ fontWeight: 500 }}
                                    >
                                        <i className="bi bi-journal-text me-2"></i>
                                        {t("aboutViewResearchPublications")}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Research Team Section */}
            {aboutData?.teamMembers && aboutData.teamMembers.length > 0 && (
                <div className="row mb-5">
                    <div className="col-lg-10 mx-auto">
                        <div className="card border-0 shadow-sm">
                            <div className="card-body p-4 p-lg-5">
                                <h3 className="text-center mb-4" style={{ color: '#112d4e', fontWeight: 600 }}>
                                    <i className="bi bi-people me-2"></i>
                                    {t("aboutResearchTeam")}
                                </h3>
                                <p className="text-center text-secondary mb-4">
                                    {t("aboutResearchTeamDescription")}
                                </p>

                                <div className="row g-4">
                                    {aboutData.teamMembers.map((member) => (
                                        <div key={member.id} className="col-md-6 col-lg-4">
                                            <div className="card h-100 border-0 bg-light">
                                                <div className="card-body text-center p-4">
                                                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                                                        style={{ width: '60px', height: '60px', backgroundColor: member.avatarColor || '#112d4e', overflow: 'hidden' }}>
                                                        {member.avatarUrl ? (
                                                            <img src={getAvatarUrl(member.avatarUrl)} alt={getLocalizedText(member, 'name')}
                                                                style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover' }} />
                                                        ) : (
                                                            <i className="bi bi-person-circle text-white" style={{ fontSize: '1.8rem' }}></i>
                                                        )}
                                                    </div>
                                                    <h6 style={{ color: '#112d4e', fontWeight: 600 }}>{getLocalizedText(member, 'name')}</h6>
                                                    <p className="text-muted small mb-1">{getLocalizedText(member, 'title')}</p>
                                                    <p className="text-muted small mb-0">
                                                        {getLocalizedText(member, 'affiliation') || getLocalizedText(member, 'contribution')}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* System Versions Section */}
            {aboutData?.systemVersions && aboutData.systemVersions.length > 0 && (
                <div className="row mb-5">
                    <div className="col-lg-10 mx-auto">
                        <div className="card border-0 shadow-sm" style={{ backgroundColor: '#f0f7ff' }}>
                            <div className="card-body p-4 p-lg-5">
                                <h3 className="text-center mb-4" style={{ color: '#112d4e', fontWeight: 600 }}>
                                    <i className="bi bi-layers me-2"></i>
                                    {t("aboutSystemVersions")}
                                </h3>
                                <p className="text-center text-secondary mb-4">
                                    {t("aboutSystemVersionsDesc")}
                                </p>
                                <div className="row g-4">
                                    {aboutData.systemVersions.map((version) => (
                                        <div key={version.id} className="col-md-6">
                                            <div className="card h-100 border-0 bg-white shadow-sm"
                                                style={version.isCurrent ? { border: '2px solid #112d4e !important' } : {}}>
                                                <div className="card-body p-4">
                                                    <div className="d-flex align-items-center mb-3">
                                                        <span className={`badge ${version.isCurrent ? 'bg-primary' : 'bg-secondary'} me-2`}
                                                            style={{ fontSize: '0.9rem' }}>{version.versionLabel}</span>
                                                        <h5 className="mb-0" style={{ color: '#112d4e' }}>{getLocalizedText(version, 'name')}</h5>
                                                        {version.isCurrent && <span className="badge bg-success ms-2">Current</span>}
                                                    </div>
                                                    <p className="text-muted small mb-3">{getLocalizedText(version, 'description')}</p>
                                                    {version.externalUrl && (
                                                        <div className="mb-2">
                                                            <small className="text-muted">
                                                                <i className="bi bi-globe me-2"></i>
                                                                <a href={version.externalUrl} target="_blank" rel="noopener noreferrer">
                                                                    {version.externalUrl}
                                                                </a>
                                                            </small>
                                                        </div>
                                                    )}
                                                    {version.techBadges && version.techBadges.length > 0 && (
                                                        <div className="mt-3">
                                                            {version.techBadges.map((badge, idx) => (
                                                                <span key={idx} className="badge me-1"
                                                                    style={{
                                                                        backgroundColor: getBadgeColor(badge.color),
                                                                        color: needsDarkText(badge.color) ? '#212529' : '#fff'
                                                                    }}>
                                                                    {badge.name}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Key Features */}
            {aboutData?.keyFeatures && aboutData.keyFeatures.length > 0 && (
                <div className="row mb-5">
                    <div className="col-12">
                        <h3 className="text-center mb-4" style={{ color: '#112d4e', fontWeight: 600 }}>
                            <i className="bi bi-stars me-2"></i>
                            {t("aboutKeyFeatures")}
                        </h3>
                        <div className="row g-4">
                            {aboutData.keyFeatures.map((feature) => (
                                <div key={feature.id} className="col-md-4">
                                    <div className="card h-100 border-0 shadow-sm">
                                        <div className="card-body text-center p-4">
                                            <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                                                style={{ width: '70px', height: '70px', backgroundColor: feature.iconBgColor || '#e8f4fd' }}>
                                                <i className={feature.iconClass || 'bi bi-star'}
                                                    style={{ fontSize: '2rem', color: feature.iconColor || '#112d4e' }}></i>
                                            </div>
                                            <h5 style={{ color: '#112d4e', fontWeight: 600 }}>{getLocalizedText(feature, 'title')}</h5>
                                            <p className="text-muted mb-0">{getLocalizedText(feature, 'description')}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* MCDM Methods */}
            {aboutData?.mcdmMethods && aboutData.mcdmMethods.length > 0 && (
                <div className="row mb-5">
                    <div className="col-lg-10 mx-auto">
                        <div className="card border-0 shadow-sm" style={{ backgroundColor: '#f8f9fa' }}>
                            <div className="card-body p-4 p-lg-5">
                                <h3 className="text-center mb-4" style={{ color: '#112d4e', fontWeight: 600 }}>
                                    <i className="bi bi-calculator me-2"></i>
                                    {t("aboutMCDMTitle")}
                                </h3>
                                <p className="text-center text-secondary mb-4">
                                    {t("aboutMCDMDescription")}
                                </p>
                                <div className="row g-3">
                                    {aboutData.mcdmMethods.map((method) => (
                                        <div key={method.id} className="col-6 col-md">
                                            <div className="text-center p-3 bg-white rounded shadow-sm">
                                                <strong style={{ color: '#112d4e' }}>{method.acronym}</strong>
                                                <p className="small text-muted mb-0 mt-1">{getLocalizedText(method, 'fullName')}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Call to Action - Only show for non-authenticated users */}
            {!isAuthenticated && (
                <div className="row mb-5">
                    <div className="col-lg-8 mx-auto text-center">
                        <div className="p-5 rounded-3" style={{
                            background: 'linear-gradient(135deg, #112d4e 0%, #1a4066 100%)',
                            color: '#fff'
                        }}>
                            <h3 className="mb-3" style={{ fontWeight: 600 }}>{t("aboutCTATitle")}</h3>
                            <p className="mb-4 opacity-75">{t("aboutCTADescription")}</p>
                            <div className="d-flex justify-content-center gap-3 flex-wrap">
                                <Link
                                    to="/ToolsCatalog"
                                    className="btn btn-light btn-lg px-4"
                                    style={{ fontWeight: 500 }}
                                >
                                    <i className="bi bi-tools me-2"></i>
                                    {t("aboutCTABrowse")}
                                </Link>
                                <Link
                                    to="/register"
                                    className="btn btn-outline-light btn-lg px-4"
                                    style={{ fontWeight: 500 }}
                                >
                                    <i className="bi bi-person-plus me-2"></i>
                                    {t("aboutCTARegister")}
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* For authenticated users, show a simpler CTA */}
            {isAuthenticated && (
                <div className="row mb-5">
                    <div className="col-lg-8 mx-auto text-center">
                        <div className="p-5 rounded-3" style={{
                            background: 'linear-gradient(135deg, #112d4e 0%, #1a4066 100%)',
                            color: '#fff'
                        }}>
                            <h3 className="mb-3" style={{ fontWeight: 600 }}>{t("aboutCTATitle")}</h3>
                            <p className="mb-4 opacity-75">{t("aboutCTADescription")}</p>
                            <div className="d-flex justify-content-center gap-3 flex-wrap">
                                <Link
                                    to="/ToolsCatalog"
                                    className="btn btn-light btn-lg px-4"
                                    style={{ fontWeight: 500 }}
                                >
                                    <i className="bi bi-tools me-2"></i>
                                    {t("aboutCTABrowse")}
                                </Link>
                                <Link
                                    to="/ToolSelection/Selection"
                                    className="btn btn-outline-light btn-lg px-4"
                                    style={{ fontWeight: 500 }}
                                >
                                    <i className="bi bi-sliders me-2"></i>
                                    {t("toolSelection")}
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AboutContent;
